import {TEticketData, TEticketEvent, TEticketOrder, TEticketTicket} from '../../../redux/eTicket/eTicket.type';
import {ETicketPage} from '../eTicketPage.type';
import CoverContent from '../CoverContent/CoverContent';
import EntranceTicketStatus from '../EntranceTicketStatus/EntranceTicketStatus';
import TicketDetails from '../TicketDetails/TicketDetails';
import TicketScanStatus from '../TicketScanStatus/TicketScanStatus';
import MemberTicket from '../MemberTicket/MemberTicket';
import MemberTicketStatus from '../MemberTicket/memberTicketStatus/MemberTicketStatus';
import {toMemberSubscription} from '../MemberTicket/memberTicket.utils';

interface BuildTicketPagesParams {
    tickets: TEticketTicket[];
    event?: TEticketEvent;
    order?: TEticketOrder;
    /* The cover slide opens the regular flow only — in a subscription order the card itself is first */
    withCover?: boolean;
}

export function buildTicketPages({tickets, event, order, withCover = true}: BuildTicketPagesParams): ETicketPage[] {
    return [
        ...(withCover ? [{key: 'entrance', withLink: false, TopComp: EntranceTicketStatus, Comp: CoverContent}] : []),
        ...tickets.map((ticket, index) => ({
            // qid, not tid — an order can hold several tickets of the same tid
            // (e.g. two seats of one price type) and keys must stay unique
            key: `ticket-${ticket.qid}`,
            withLink: false,
            // browser-print includes scanned (burned) tickets but never canceled ones
            printable: !ticket.canceled,
            ...(event ? {
                TopComp: ticket.canceled ? undefined : TicketScanStatus,
                topProps: {ticket},
                Comp: TicketDetails,
                props: {
                    ticket,
                    index,
                    total: tickets.length,
                    event,
                    order
                }
            } : {})
        }))
    ];
}

/* One subscription card per series, in a single flat sequence — no separate navigation between series.
   The API keys the subscription block by planTid and returns the same, already accumulated block on every
   ticket of that series, so several subscriptions of one series in an order are one card — not one each. */
export function buildSubscriptionPages(tickets: TEticketTicket[], data: TEticketData): ETicketPage[] {
    const bySeries = new Map<number, TEticketTicket>();
    tickets.forEach(ticket => {
        const planTid = ticket.subscription?.plan?.planTid ?? ticket.tid;
        if(!bySeries.has(planTid)) {
            bySeries.set(planTid, ticket);
        }
    });

    return Array.from(bySeries.values()).map(ticket => {
        const subscription = toMemberSubscription(ticket, data);
        return {
            key: `subscription-${ticket.qid}`,
            withLink: false,
            printable: subscription.status !== 'canceled',
            TopComp: MemberTicketStatus,
            topProps: {subscription},
            Comp: MemberTicket,
            props: {subscription}
        };
    });
}
